import Vue from 'vue';

/**
 * Waypoint handler that is meant to be used inside a Vue instance.
 *
 * @param {object} options to used for constructing a waypoint
 * @param {function} shouldUpdateFunction a function used in the update method to decide
 *                                        if it should proceed to construct a new waypoint
 * Usage:
 * - The developer has the reponsible manage the lifecycle of waypoint with the provided methods:
 *   `create`, `update`, and `destroy`
 * - If the waypoint position may change, the existing waypoint need to be invalidated.
 *     - This can be done manually by calling `destroy`
 *     - Or be done automatically by supplying destroyOnTrigger in the options.
 * - Waypoint can be revalidated via the `update` method
 */
export default class WaypointHandler {
  constructor(options, shouldUpdateFunction) {
    // Set up the waypoint options
    if (typeof options.handler !== 'function') {
      throw new Error('Missing handler. Please provide options.handler for the waypoint');
    }
    this.options = options;
    this.options.element = this.options.element || document.body;
    this.options.offset = this.options.offset || 'bottom-in-view';

    if (this.options.destoyOnTrigger) {
      const handlerFn = this.options.handler;
      this.options.handler = function handler() {
        this.destroy();
        handlerFn();
      };
    };

    // If no function is specified, it will be assigned to generate a truthy
    this.shouldUpdateFunction = (typeof shouldUpdateFunction === 'function')
      ? shouldUpdateFunction
      : () => true
    ;

    // Create the the waypoint
    this.waypoint = null;
    this.create();
  }

  create(shouldCheckAgainstCondition = false) {
    Vue.nextTick(() => {
      const shouldByPassCheck = !shouldCheckAgainstCondition;

      if (shouldByPassCheck || this.shouldUpdateFunction()) {
        this.waypoint = new Waypoint(this.options);
      }
    });
  }

  destroy() {
    this.waypoint.destroy();
  }

  update() {
    this.destroy();
    this.create(true);
  }
}
